import { useCallback } from 'react'
import type { JSX } from 'react'

import {
  Button,
  CheckIcon,
  Heading,
  Image,
  List,
  Text,
  UpgradeDialog,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'

import styles from './SavedRepliesPaywal.module.css'

type CtaOptions = {
  view: string
  location: string
  button: string
}

type SavedRepliesPaywallProps = {
  open: boolean
  setOpen: (open: boolean) => void
  ctaProperties: CtaOptions
  upgradePathName: string
}

export function SavedRepliesPaywall({
  open,
  setOpen,
  ctaProperties,
  upgradePathName,
}: SavedRepliesPaywallProps): JSX.Element {
  const currentOrganizationId = useOrganizationId()
  const openGlobalModal = useOpenGlobalModal()

  const { view, location, button } = ctaProperties
  const cta = `publish-${view}-${location}-${button}-1` as const

  const SAVED_REPLIES_PAYWALL_DATA = {
    heading: 'Upgrade to create more Saved Replies',
    body: 'Save information you share often to quickly insert it when replying to your comments.',
    bulletPoints: [
      'Unlimited saved replies',
      'Unlimited comment reply suggestions',
      'Schedule up to 2000 posts at a time',
      'Advanced publishing and planning features',
      'In-depth social analytics',
    ],
    ctaText: 'See Upgrade Options',
  }

  const onCTAClick = (): void => {
    BufferTracker.cTAClicked({
      clientName: Client.PublishWeb,
      product: 'publish',
      cta,
      upgradePathName,
      organizationId: currentOrganizationId ?? '',
    })

    setOpen(false)
    openGlobalModal(MODALS.planSelector, {
      cta,
      upgradePathName,
      planSelector: {
        initialStep: 'upgrade',
      },
    })
  }

  const handleOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        BufferTracker.upgradePathViewed({
          clientName: Client.PublishWeb,
          product: 'publish',
          organizationId: currentOrganizationId ?? '',
          upgradePathName,
          cta,
        })
      }
      setOpen(nextOpen)
    },
    [setOpen, cta, upgradePathName, currentOrganizationId],
  )

  return (
    <UpgradeDialog
      open={open}
      onOpenChange={(nextOpen) => handleOpen(nextOpen)}
    >
      <UpgradeDialog.Content size="xlarge">
        <UpgradeDialog.TextContent>
          <Heading size="lg">{SAVED_REPLIES_PAYWALL_DATA.heading}</Heading>
          <Text>{SAVED_REPLIES_PAYWALL_DATA.body}</Text>
          <List className={styles.paywallList}>
            {SAVED_REPLIES_PAYWALL_DATA.bulletPoints.map((item) => {
              return (
                <List.Item key={item} icon={CheckIcon}>
                  {item}
                </List.Item>
              )
            })}
          </List>
          <Button variant="primary" size="large" onClick={onCTAClick}>
            {SAVED_REPLIES_PAYWALL_DATA.ctaText}
          </Button>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            src="https://buffer-publish.s3.amazonaws.com/images/Upgrade-Comments-Suggestions/upgrade-comments-suggestions.png"
            alt="Upgrade to have unlimited reply suggestions"
            placeholder="https://buffer-publish.s3.amazonaws.com/images/Upgrade-Comments-Suggestions/upgrade-comments-suggestions.png"
            className={styles.paywallImage}
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
